import {_} from 'lodash';
import * as ko from "knockout";
import {
    ICompany,
    ICompanySyncstep,
    IFormTemplate,
    IMessage,
    IMessageData,
    IMessageType,
    IRouteDefinitionParams,
    IRuleset,
    ISyncField,
    TSyncfield
} from "../../index";
import PromiseRequest from "../../req/PromiseRequest";
import {DEFAULT_SMS_SENDER, MAX_DESCRIPTION_LENGTH, ObjectTypeId, SMS_ABSOLUTE_LIMIT, SMS_LIMIT} from "../../constants";
import {IMessageMutable} from "./index";
import {global, lm} from "../../appContext";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {_d, createErrorMessage, escapeHTML, validateEmail} from "../../util/misc";
import {nsFa} from "../fa/fn";
import {MutableMessageType} from "./MutableMessageType";
import {nsMessages} from "./fn";
import {getCancelButton, getSubmitButton, setRouteHash} from "../../constants/RouteDefinitions";
import {nsRoute} from "../../util/route";
import {ButtonManager} from "../../util/ButtonManager";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {ErrorManager} from "../../error/ErrorManager";
import {ErrorObject} from "../../error/ErrorObject";
import {IComboboxOptions_Ruleset} from "../combobox/ruleset";
import {IComboboxOptions_syncfield} from "../combobox/syncfield";
import i = nsFa.i;
import getAddRecipientLabel = nsMessages.getAddRecipientLabel;
import TypeChecker = nsMessages.TypeChecker;
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import ButtonOrder = nsRoute.ButtonOrder;
import mutable_template_rulesetHtml from './mutable-template-ruleset.html?raw';
import mutableHtml from './mutable.html?raw';

export const tplRuleset: string = mutable_template_rulesetHtml;
export const tplRaw: string = mutableHtml;
export const template: string = tplRaw.replace('<!-- TEMPLATE_RULESET -->', tplRuleset);

export abstract class MutableMessageTemplate extends ParsedRouteDataDecorator {
    private ObjectTypeId = ObjectTypeId;
    private readonly MAX_DESCRIPTION_LENGTH = MAX_DESCRIPTION_LENGTH;
    private readonly pv = global.permissionValidator;

    protected abstract submitSuccessMessage: string;
    protected readonly currentLimit: KnockoutObservable<number> = ko.observable(SMS_LIMIT);
    protected readonly formTemplate: KnockoutObservable<IFormTemplate | null> = ko.observable(null)
    protected readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    protected abstract req_submit: PromiseRequest;
    protected readonly req_getcompany: PromiseRequest = global.getPromiseRequest("getcompany");
    protected readonly req_getcompanysyncsteps: PromiseRequest = global.getPromiseRequest("getcompanysyncsteps");
    protected readonly req_getmessagetypes = global.getPromiseRequest('getmessagetypes');

    protected data: IMessageMutable = {
        $original: {
            active: false,
            addrecipient: "",
            body: "",
            companyid: "",
            description: '',
            id: 0,
            logcount: 0,
            messagelog: [],
            messagetypeid: 0,
            messagetypename: '',
            messagetype: null!,
            name: "",
            recipientfield: "",
            rulesetid: 0,
            rulesetname: "",
            sender: '',
            subject: "",
            syncstepid: 0,
            syncstepname: ''
        } as unknown as IMessage,
        active: ko.observable(false),
        addrecipient: ko.observable(""),
        body: ko.observable(""),
        description: ko.observable(""),
        messagetype: ko.observable(null),
        name: ko.observable(""),
        recipientfield: ko.observable(null),
        ruleset: ko.observable(null),
        sender: ko.observable(''),
        subject: ko.observable(""),
        syncstep: ko.observable(null),
    };

    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    private SMS_LIMIT: number = SMS_LIMIT;

    protected mutableMessageType!: MutableMessageType;
    protected companySyncsteps!: ICompanySyncstep[];
    protected company!: ICompany;

    protected pageButtons: ButtonManager = new ButtonManager()
    protected availableSmsSenders!: string[]; // populated from company data

    private err: Record<keyof Omit<IMessageMutable, '$original' | 'active'>, ErrorObject> = {
        addrecipient: new ErrorObject(),
        body: new ErrorObject(),
        description: new ErrorObject(),
        messagetype: new ErrorObject(),
        name: new ErrorObject(),
        recipientfield: new ErrorObject(),
        ruleset: new ErrorObject(),
        sender: new ErrorObject(),
        subject: new ErrorObject(),
        syncstep: new ErrorObject(),
    }

    private errManager: ErrorManager = new ErrorManager()

    constructor(params: IRouteDefinitionParams) {
        super(params);

        this.err.messagetype.addValidator({
            fn: () => !!this.data.messagetype(),
            message: `((invalid)) ((type|ac))`,
        })

        this.err.name.addValidator({
            fn: () => !!this.data.name(),
            message: `((invalid)) ((name|ac))`,
        }, {
            fn: () => !/[<>]/gm.test(this.data.name()),
            message: `((contains invalid characters))`,
        })

        this.err.description.addValidator({
            fn: () => !/[<>]/gm.test(this.data.description()),
            message: `((contains invalid characters))`,
        })

        this.err.subject.addValidator({
            fn: () => !!this.data.subject(),
            message: () => this.isType('email')
                ? '((invalid)) ((subject|ac))'
                : '((invalid)) ((form template|ac))',
            prerequisite: () => this.isType('email', 'task', 'message'),
        }, {
            fn: () => !/[<>]/gm.test(this.data.subject()),
            message: `((contains invalid characters))`,
        })

        this.err.body.addValidator({
            fn: () => !!this.data.body(),
            message: '((invalid)) ((message body|ac))',
        }, {
            fn: () => this.data.body().length <= this.currentLimit(),
            message: '((invalid)) ((length|ac))',
            prerequisite: () => this.isType('sms'),
        }, {
            fn: () => !/[<>]/gm.test(this.data.body()),
            message: `((contains invalid characters))`,
            prerequisite: () => this.isType('sms'),
        })

        this.err.ruleset.addValidator({
            fn: () => !!this.data.ruleset(),
            message: 'invalid ruleset',
            prerequisite: () => !this.isRulesetOptional(),
        })

        this.err.recipientfield.addValidator({
            fn: () => !!this.data.recipientfield(),
            message: '((invalid)) ((recipientfield|ac))',
            prerequisite: () => this.isType('sms', 'email') && !this.isType('custom'),
        })

        this.err.addrecipient.addValidator({
            fn: () => this.validateAddRecipient(),
            message: '((invalid)) ((recipient|ac))',
        })

        this.err.sender.addValidator({
            fn: () => {
                if (this.isType('email')) {
                    return validateEmail(this.data.sender())
                }
                if (this.isType('sms')) {
                    return !!this.data.sender()
                }
                return true;
            },
            message: '((invalid)) ((sender|ac))',
        })

        this.err.syncstep.addValidator({
            fn: () => this.objecttypeid() == ObjectTypeId.FormData || !!this.data.syncstep(),
            message: '((invalid)) ((synchronization step|ac))',
            prerequisite: () => !global.pv.superAdmin(),
        })

        this.errManager.addErrorObject(this.err.addrecipient)
        this.errManager.addErrorObject(this.err.body)
        this.errManager.addErrorObject(this.err.description)
        this.errManager.addErrorObject(this.err.messagetype)
        this.errManager.addErrorObject(this.err.name)
        this.errManager.addErrorObject(this.err.recipientfield)
        this.errManager.addErrorObject(this.err.ruleset)
        this.errManager.addErrorObject(this.err.sender)
        this.errManager.addErrorObject(this.err.subject)
        this.errManager.addErrorObject(this.err.syncstep)

        this.setupSubscriptions()
        this.setupPageButtons()
    }

    protected setupSubscriptions() {
        this.kosub.subscribe(this.data.body, () => {
            if (this.data.body()?.length <= SMS_LIMIT) {
                this.currentLimit(SMS_LIMIT);
            }
        });

        this.kosub.subscribe(this.data.messagetype, (val: IMessageType | null) => {
            if (!val) {
                return;
            }

            if (this.isType('task')) {
                if (isNaN(Number(this.data.subject()))) {
                    this.data.subject('');
                }
            }
            // are we about to change to HTML (true) or non-HTML (false) content?
            if (this.isType('sms', 'email', 'message')) {
                let body = String(this.data.body());
                if (val.ishtml) {
                    // going from non-HTML to HTML content
                    body = body.replace(/(.*)\s*\n{2,}/mg, (substring: string, p1: string) => `${p1}<br><br>`);
                } else {
                    // going from HTML to non-HTML content
                    const div = document.createElement("div");
                    div.innerHTML = body;
                    body = div.innerText;
                    body = body.replace(/(.*)\s*\n{2,}/mg, (substring: string, p1: string) => `${p1}\n\n`);
                }
                const isReady = this.isReady()
                this.isReady(false)
                this.data.body(body.trim());
                this.isReady(isReady)
            }
        });
    }

    protected setupPageButtons() {
        const cancelButton = getCancelButton();
        if (cancelButton) {
            this.pageButtons.add(cancelButton);
        }
        this.pageButtons.add({
            onClick: this.onPreviewClick.bind(this),
            enabled: this.isFormValid.bind(this),
            icon: 'fa-eye',
            label: 'preview',
            cssClass: 'action',
            order: ButtonOrder.PREVIEW,
        })
        this.pageButtons.add(getSubmitButton({
            attr: {type: 'submit'},
            onClick: this.onSubmit.bind(this),
            enabled: this.isFormValid.bind(this),
        }))
    }

    /***************************************************************************************************************
     * Initialization methods
     **************************************************************************************************************/
    protected init(): Promise<void> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        return Promise
            .resolve()
            .then(() => this.req_getcompany
                .request()
                .then((data: ICompany[]) => {
                    const company = data[0];
                    if (!company) {
                        return;
                    }
                    this.company = company;
                    const smssenders = this.company.smssenders;
                    this.availableSmsSenders = (smssenders ?? '').split(',').filter(Boolean);
                    if (!this.availableSmsSenders.includes(DEFAULT_SMS_SENDER)) {
                        this.availableSmsSenders.push(DEFAULT_SMS_SENDER);
                    }
                }))
            .then(() => this.req_getcompanysyncsteps
                .request()
                .then((data: ICompanySyncstep[]) => {
                    this.companySyncsteps = data
                }))
    };

    protected initAfter(): Promise<void> {
        return Promise.resolve()
            .then(() => {
                this.messageData(null);
                this.isReady(true);
            })
    }

    protected initError(reason: string) {
        this.messageData(createErrorMessage(String(reason)))
    }

    protected initMessageType(): Promise<void> {
        return this.req_getmessagetypes
            .request()
            .then((data: IMessageType[]) => {
                this.mutableMessageType = new MutableMessageType(this.data.messagetype, data);
            });
    }

    protected initSyncStep() {
        const sorted = _.sortBy(this.companySyncsteps, 'order');
        const last = _.last(sorted);
        if (!last?.syncstep) {
            return;
        }
        this.data.$original.syncstepid = last.syncstep.id;
        this.data.syncstep(last.syncstep);
    }

    protected showAlert: KnockoutComputed<boolean> = ko.pureComputed(() => {
        return this.isType('sms')
    });

    /***************************************************************************************************************
     * Form validation
     **************************************************************************************************************/

    protected isFormValid() {
        return this.errManager.validate()
    }

    private validateAddRecipient() {
        const recipients = this.data.addrecipient();
        const isEmpty = recipients === '';
        if (isEmpty && this.isType('custom')) {
            return false
        }
        const arrRecipients = nsMessages.splitRecipients(recipients)
        return arrRecipients.every(this.validateRecipient.bind(this));
    }

    private validateRecipient(str: string): boolean {
        if (!str) {
            return false
        }
        switch (this.typeChecker.getType()?.[0]) {
            case 'email':
            case 'sms': {
                if (str.includes('[')) {
                    return true
                }
            }
        }
        switch (this.typeChecker.getType()?.[0]) {
            case 'email': {
                return validateEmail(str);
            }
            case 'sms': {
                const a = str.replace(/[+ ]/g, '').length >= 8
                const b = /^\+?[0-9 ]+$/.test(str)
                return a && b
            }
            case 'task':
            case 'message': {
                const val = Number(str);
                return Number.isInteger(val);
            }
            default:
                _d.warn(`Can't validate recipient; unknown type.`);
                return false;
        }
    }

    /***************************************************************************************************************
     * View interaction
     **************************************************************************************************************/

    private onSMSExpandClick() {
        global.dialogData({
            title: `${i("fa-plus")} ((expand message))`,
            body: `message: expand message|limit:${SMS_LIMIT}`,
            confirmLabel: "yes, continue",
            cancelLabel: "no, cancel",
            onConfirm: () => this.currentLimit(SMS_ABSOLUTE_LIMIT),
        });
    }

    onSubmit() {
        this.isReady(false);

        const data = this.getSubmitData();
        data.body = encodeURIComponent(data.body)
        const jmessage = JSON.stringify(data);

        this.messageData({message: REQUEST_MESSAGE});

        this.req_submit.abort();
        this.req_submit
            .request({jmessage: jmessage})
            .then((data: IMessage[]) => {
                global.cacheManager.removeCache('getruleset');
                global.cacheManager.removeCache('searchruleset');
                global.cacheManager.removeCache("searchmessage");
                global.cacheManager.removeCache("getmessage");
                global.cacheManager.removeCache('getehubprocess');

                // changes to the message flow could have changed one or more expressions in the subject/body -
                // therefore remove chache of any expressions evaluated
                global.cacheManager.removeCache('evaluateexpression');

                global.snackbarData({message: this.submitSuccessMessage});
                const message = data[0];
                if (!message) {
                    return;
                }
                setRouteHash('messageflowdetails', {id: message.id});
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                this.isReady(true);
            });
    }

    protected onPreviewClick() {
        const name = escapeHTML(this.data.name())
        global.dialogManager.addComponent({
            title: `${i('fa-eye')} ((preview message)) [${name}]`,
            fullWidth: true,
            content: {
                name: 'component/messages/preview',
                params: {message: this.getSubmitData()},
            }
        });
    }

    protected getComboboxRulesetParams(): IComboboxOptions_Ruleset {
        return {
            comboboxOptions: {
                prepareData: {
                    filter: (o: IRuleset) => this.availableObjectTypeIds().includes(o.objecttype?.id) || o.id == this.data.$original.rulesetid,
                    sort: (arr: IRuleset[]) => _.sortBy(arr, (o: IRuleset) => o.name.trim().toLowerCase()),
                },
                inputAttrs: {placeholder: lm.t('choose|td')},
                initialMatch: o => o.id == this.data.$original.rulesetid,
                selected: this.data.ruleset,
                onItemsPrepared: (items) => {
                    const current = this.data.ruleset()
                    const rulesetId = this.data.$original?.rulesetid
                    if (current && rulesetId && !items.some(o => o.id === rulesetId)) {
                        items.push(current)
                    }
                    return items
                },
                inputStatus: (sel, text) => {
                    if (this.isRulesetOptional()) {
                        return sel || !text ? 'ok' : 'warning'
                    }
                    return sel ? 'ok' : 'warning'
                },
            },
            objectTypeIds: this.availableObjectTypeIds,
        }
    }

    /***************************************************************************************************************
     * Submit
     **************************************************************************************************************/

    protected getSubmitData(): IMessage {
        const d: IMessageMutable = this.data;
        const b = this.objecttypeid() == ObjectTypeId.FormData;
        let syncstepid: number, syncstepname: string;

        if (!b) {
            const syncstep = d.syncstep();
            syncstepid = syncstep?.id ?? 0;
            syncstepname = syncstep?.name ?? '';
        } else {
            syncstepid = 0;
            syncstepname = '';
        }

        return {
            active: d.active(),
            addrecipient: d.addrecipient(),
            body: d.body(),
            description: d.description(),
            companyid: d.$original.companyid || global.login.selectedOrganizationId() || '',
            id: d.$original.id,
            logcount: 0,
            messagelog: [], // set this to empty to avoid a potentially huge payload (it won't be changed anyways)
            messagetypeid: d.messagetype()?.id ?? d.$original.messagetypeid,
            messagetypename: d.messagetype()?.name ?? d.$original.messagetypename,
            messagetype: d.messagetype() ?? d.$original.messagetype,
            name: d.name(),
            recipientfield: d.recipientfield() || "",
            rulesetid: d.ruleset()?.id ?? 0,
            rulesetname: d.ruleset()?.name ?? '',
            sender: this.data.sender(),
            subject: d.subject(),
            syncstepid: syncstepid,
            syncstepname: syncstepname
        };
    }

    /***************************************************************************************************************
     * Viewmodel computed and helpers
     **************************************************************************************************************/

    protected readonly objecttypeid = ko.pureComputed((): ObjectTypeId | null => {
        const ruleset = this.data.ruleset();
        if (!ruleset?.objecttype) {
            return null;
        }
        return ruleset.objecttype.id;
    });

    private readonly objectTypeIds: KnockoutComputed<ObjectTypeId[]> = ko.pureComputed((): ObjectTypeId[] => {
        const ret = [ObjectTypeId.Function, ObjectTypeId.FunctionAlias, ObjectTypeId.History];
        if (this.isType('task')) {
            ret.push(ObjectTypeId.FormData);
        }
        return ret;
    });

    protected typeChecker = new TypeChecker(this.data.messagetype);
    protected isType = this.typeChecker.isType.bind(this.typeChecker);

    private fnFilterRecipientField(sf: ISyncField): boolean {
        if (this.isType('sms')) {
            const syncfieldKeys: TSyncfield[] = ['Mobile', 'PrivatePhone', 'TelephoneNumber', 'WorkPhone'];
            return syncfieldKeys.includes(sf.name);
        }

        if (this.isType('email')) {
            if (this.objecttypeid() === ObjectTypeId.ClientCredential) {
                return sf.name === 'ExtensionAttribute6';
            }
            const syncfieldKeys: TSyncfield[] = ['Email', 'Mail', 'PrivateEmail', 'Upn'];
            return syncfieldKeys.includes(sf.name);
        }
        return false;
    }

    private addRecipientLabel = ko.pureComputed((): string => {
        return getAddRecipientLabel(this.data.messagetype()?.id ?? this.data.$original.messagetypeid);
    });

    private availableObjectTypeIds = ko.pureComputed((): ObjectTypeId[] => {
        const keys = this.mutableMessageType.getKeys()

        if (keys.includes('eadm')) {
            return [ObjectTypeId.User, ObjectTypeId.FormData]
        }

        if (keys.includes('user') || keys.includes('manager')) {
            return [ObjectTypeId.User]
        }

        return [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group, ObjectTypeId.FormData, ObjectTypeId.ClientCredential]
    })

    protected isRulesetOptional = ko.pureComputed((): boolean => {
        return this.isType('email') && this.isType('custom')
    })

    private getComboboxParams_recipientfield(): IComboboxOptions_syncfield {
        return {
            comboboxOptions: {
                prepareData: {
                    filter: o => this.fnFilterRecipientField(o),
                },
                initialMatch: o => o.name == (this.data.recipientfield() ?? ''),
                onSelect: o => this.data.recipientfield(o?.name ?? null),
                inputStatus: (sel) => sel ? 'ok' : 'warning',
            },
            objectTypeIds: this.objecttypeid() ?? ObjectTypeId.User,
        }
    }

    /***************************************************************************************************************
     * Miscellaneous
     **************************************************************************************************************/

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        this.req_getcompany.abort()
        this.req_getcompanysyncsteps.abort()
        this.req_getmessagetypes.abort()
        this.req_submit.abort()
    }

}
